import { toast } from 'react-toastify';

import { apiSlice } from '../apiSlice';
import invalidateTags from '../invalidateTags';
import axios from '../../utils/axios';


export const tags = {
  application: 'application',
  applications: 'applications',
  homeDetails: 'homeDetails',
  homeOwners: 'homeOwners',
  status: 'status',
};

const applicationApi = apiSlice
  .enhanceEndpoints({ addTagTypes: Object.values(tags) })
  .injectEndpoints({
    endpoints: (build) => ({

      // Get Applicant Profile
      getApplications: build.query({
        queryFn: async () => {
          try {
            const response = await axios({
              url: `${baseURL}/applications`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.applications],
        keepUnusedDataFor: 0,
      }),



      // Get Applicant UUID
      getApplicationByUUID: build.query({
        queryFn: async ({ application_uuid }) => {
          try {
            const response = await axios({
              url: `${baseURL}/applications/${application_uuid}`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.application],
        keepUnusedDataFor: 0,
      }),



      getVerifiedAddresses: build.query({
        queryFn: async ({ data }) => {
          try {
            const response = await axios({
              url: `${baseURL}/validate-address`,
              method: 'POST',
              data,
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            toast.error('It looks like something went wrong. Please try again.');
            return { error };
          }
        },
        providesTags: [],
      }),



      getHomeDetails: build.query({
        queryFn: async ({ application_uuid }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-details/${application_uuid}`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.homeDetails, tags.application],
      }),



      updateIsNewApplication: build.mutation({
        queryFn: async ({ application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/applications/${application_uuid}/mark-not-new`,
              method: 'PUT',
            });
            if (cb) cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.application]),
      }),



      updateHomeDetails: build.mutation({
        queryFn: async ({ data, setSubmitting, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-details/${application_uuid}`,
              method: 'PUT',
              data,
            });
            toast.success('Updated!');
            if (cb) cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            setSubmitting();
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeDetails, tags.application]),
      }),



      updateMortgageLien: build.mutation({
        queryFn: async ({ data, setSubmitting, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-mortgage-lien/${application_uuid}`,
              method: 'PUT',
              data,
            });
            toast.success('Updated!');
            if (cb) cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            setSubmitting();
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeDetails, tags.application]),
      }),



      updateInsurance: build.mutation({
        queryFn: async ({ data, setSubmitting, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-insurance/${application_uuid}`,
              method: 'PUT',
              data,
            });
            toast.success('Updated!');
            if (cb) cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            setSubmitting();
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeDetails, tags.application]),
      }),



      getAllHomeowners: build.query({
        queryFn: async ({ application_uuid }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-owners/${application_uuid}`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.homeOwners, tags.application],
      }),



      getHomeownerByUUID: build.query({
        queryFn: async ({ homeowner_uuid }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-owners/${homeowner_uuid}/details`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.homeOwners, tags.application],
      }),



      addHomeowners: build.mutation({
        queryFn: async ({ data, setSubmitting, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-owners/${application_uuid}`,
              method: 'POST',
              data,
            });
            toast.success('Created!');
            cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            setSubmitting();
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeOwners, tags.application]),
      }),



      updateHomeowners: build.mutation({
        queryFn: async ({ homeowner_uuid, data, setSubmitting, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-owners/${homeowner_uuid}/${application_uuid}`,
              method: 'PUT',
              data,
            });
            toast.success('Updated!');
            cb();
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            setSubmitting();
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeOwners, tags.application]),
      }),



      deleteHomeowners: build.mutation({
        queryFn: async ({ homeowner_uuid, application_uuid, cb }) => {
          try {
            const response = await axios({
              url: `${baseURL}/application-home-owners/${homeowner_uuid}/${application_uuid}`,
              method: 'DELETE',
            });
            cb();
            toast.success('Deleted!');
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        invalidatesTags: invalidateTags([tags.homeOwners, tags.application]),
      }),



      getStatus: build.query({
        queryFn: async (application_uuid) => {
          try {
            const response = await axios({
              url: `${baseURL}/status/${application_uuid}`,
              method: 'GET',
            });
            return { data: response.data };
          } catch (error) {
            console.error(error.response);
            return { error };
          }
        },
        providesTags: [tags.status],
      }),
    }),
  });



export const {
  useGetApplicationsQuery,
  useGetApplicationByUUIDQuery,
  useLazyGetVerifiedAddressesQuery,
  useUpdateIsNewApplicationMutation,

  useGetHomeDetailsQuery,
  useUpdateHomeDetailsMutation,
  useUpdateMortgageLienMutation,
  useUpdateInsuranceMutation,

  useGetAllHomeownersQuery,
  useGetHomeownerByUUIDQuery,
  useAddHomeownersMutation,
  useUpdateHomeownersMutation,
  useDeleteHomeownersMutation,

  useGetStatusQuery,
} = applicationApi;
